/**
 * Somewhere to let.
 *
 * The codes are the whole of ISO 3166-1 alpha-2 as one string, and the names
 * come from the browser's own Intl.DisplayNames — which means the list is in
 * the reader's language ("Deutschland" for a German browser) and costs about
 * six hundred bytes rather than the twenty kilobytes a hand-written table of
 * English names would.
 *
 * Why the full list and not only the countries PROVIA has guidance for: being
 * asked "where do you let?" and not finding your own country in the list is a
 * worse experience than any wording we could have shown, and the answer is
 * still worth recording — it is what a later release needs in order to have
 * something specific to say. See 36_Jurisdictions.gs, which answers generically
 * for every code it does not know rather than guessing.
 */
const CODES = (
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ '
  + 'BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR '
  + 'CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR '
  + 'GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU '
  + 'ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ '
  + 'LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ '
  + 'MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF '
  + 'PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI '
  + 'SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR '
  + 'TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW'
).split(' ');

/*
 * Built once. Intl.DisplayNames is a real object with real setup cost, and a
 * select rendering two hundred and fifty options would otherwise pay it two
 * hundred and fifty times on every keystroke elsewhere in the form.
 */
const displayNames = (() => {
  try {
    return new Intl.DisplayNames(undefined, { type: 'region' });
  } catch {
    // Very old browsers, and the odd locked-down embedded one. The code alone
    // is a poor label but an honest one, and nothing breaks.
    return null;
  }
})();

export function countryName(code: string): string {
  const upper = String(code || '').toUpperCase();
  if (!upper) return '';
  try {
    return displayNames?.of(upper) ?? upper;
  } catch {
    return upper;
  }
}

/** Every country, named in the reader's language, in their alphabetical order. */
export const COUNTRIES: { code: string; name: string }[] = CODES
  .map((code) => ({ code, name: countryName(code) }))
  .sort((a, b) => a.name.localeCompare(b.name));
